import { useNavigate, useLocation } from "react-router-dom";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import MainTable from "../../../components/tables/MainTable";
import endpoints from "../../../services/endpoints";
import { TableDetailsItem } from "../../../components/tables/tableFunctions";
import { useEffect, useState, useCallback, useMemo } from "react";
import usePrivilege from "../../../components/CustomHooks/usePrivilege";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { showToast } from "../../../components/toastMessage/toastSlice";
import useApi from "../../../services/useApi";
import { setPreviousUrl, setUserUrl } from "../../../store/slices/geolocationSlice";
import { setUserManagementType } from "../../../store/slices/activeListSlice";
import { resetPhoneNumbers } from "../../../store/slices/phoneNumbersSlice";

interface UserManagementProps {
  type: string;
  testingProp?: boolean;
}

interface PrivilegeObject {
  add?: boolean;
  list?: boolean;
  activateDeactivate?: boolean;
  view?: boolean;
  edit?: boolean;
  flaggingDistributor?: boolean;
  distributorTransactions?: boolean;
}

export default function ListOfMasterDistributors({ type, testingProp }: UserManagementProps) {
  const navigation = useNavigate();
  const location = useLocation();
  let currentUrl = location.pathname + location.search;
  const dispatch = useDispatch<AppDispatch>();
  const { PatchAPI } = useApi();
  const { masterDistributorsList, distributorsList, subDistributorsList, agentsList, retailerList, customerList } = endpoints;
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>('user name');
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [fetchListTrigger, setFetchListTrigger] = useState(false);

  // Create a custom hook for handling privileges based on type
  function useTypeBasedPrivileges(currentType: string): PrivilegeObject {
    // Master Distributor privileges - always initialize these
    const mdmAdd = usePrivilege('MDM4');
    const mdmList = usePrivilege('MDM1');
    const mdmActivateDeactivate = usePrivilege("MDM6");
    const mdmView = usePrivilege("MDM2");
    const mdmEdit = usePrivilege("MDM5");

    // Distributor privileges - always initialize these
    const dmAdd = usePrivilege("DM4");
    const dmList = usePrivilege("DM1");
    const dmActivateDeactivate = usePrivilege("DM5");
    const dmView = usePrivilege("DM6");
    const dmEdit = usePrivilege("DM2");
    const dmFlagging = usePrivilege("DM3");
    const dmTransactions = usePrivilege("DM7");

    // Distributor privileges - always initialize these
    const sdmAdd = usePrivilege("SDM4");
    const sdmList = usePrivilege("SDM1");
    const sdmActivateDeactivate = usePrivilege("SDM2");
    const sdmView = usePrivilege("SDM6");
    const sdmEdit = usePrivilege("SDM5");
    const sdmFlagging = usePrivilege("SDM3");

    // Agent privileges - always initialize these
    const agentAdd = usePrivilege("AM4");
    const agentList = usePrivilege("AM1");
    const agentActivateDeactivate = usePrivilege("AM5");
    const agentView = usePrivilege("AM6");
    const agentEdit = usePrivilege("AM2");
    const agentFlagging = usePrivilege("AM3");
    const agentTransaction = usePrivilege('AM7')

    // Agent privileges - always initialize these
    const retailerAdd = usePrivilege("RM4");
    const retailerList = usePrivilege("RM1");
    const retailerActivateDeactivate = usePrivilege("RM5");
    const retailerView = usePrivilege("RM6");
    const retailerEdit = usePrivilege("RM2");
    const retailerFlagging = usePrivilege("RM3");
    const retailerTransaction = usePrivilege('RM7');

    // Customer privileges - always initialize these
    const customerAdd = usePrivilege("CM4");
    const customerList = usePrivilege("CM1");
    const customerActivateDeactivate = usePrivilege("CM5");
    const customerView = usePrivilege("CM6");
    const customerEdit = usePrivilege("CM2");
    const customerFlaging = usePrivilege("CM3");
    const customerTransaction = usePrivilege('CM7');
    // Return the appropriate privilege object based on type
    return useMemo(() => {
      if (currentType === 'Master Distributors') {
        return {
          add: mdmAdd,
          list: mdmList,
          activateDeactivate: mdmActivateDeactivate,
          view: mdmView,
          edit: mdmEdit,
        };
      } else if (currentType === 'Distributors') {
        return {
          add: dmAdd,
          list: dmList,
          activateDeactivate: dmActivateDeactivate,
          view: dmView,
          edit: dmEdit,
          flaggingDistributor: dmFlagging,
          distributorTransactions: dmTransactions
        };
      } else if (currentType === 'Sub Distributors') {
        return {
          add: sdmAdd,
          list: sdmList,
          activateDeactivate: sdmActivateDeactivate,
          view: sdmView,
          edit: sdmEdit,
          flaggingDistributor: sdmFlagging,
        };
      } else if (currentType === 'Agents') {
        return {
          add: agentAdd,
          list: agentList,
          activateDeactivate: agentActivateDeactivate,
          view: agentView,
          edit: agentEdit,
          flaggingDistributor: agentFlagging,
          transaction: agentTransaction
        };
      } else if (currentType === 'Retailers') {
        return {
          add: retailerAdd,
          list: retailerList,
          activateDeactivate: retailerActivateDeactivate,
          view: retailerView,
          edit: retailerEdit,
          flaggingDistributor: retailerFlagging,
          transaction: retailerTransaction,
        };
      } else if (currentType === 'Customers') {
        return {
          add: customerAdd,
          list: customerList,
          activateDeactivate: customerActivateDeactivate,
          view: customerView,
          edit: customerEdit,
          flagingCustomer: customerFlaging,
          transaction: customerTransaction
        };
      }
      // Default empty object as fallback
      return {};
    }, [
      currentType,
      mdmAdd, mdmList, mdmActivateDeactivate, mdmView, mdmEdit,
      dmAdd, dmList, dmActivateDeactivate, dmView, dmEdit, dmFlagging, dmTransactions
    ]);
  }

  // Use the custom hook
  const privilegeObject = useTypeBasedPrivileges(type);

  const handleCurrentTab = () => {
    dispatch(setUserManagementType(type));
    if (type === 'Master Distributors') {
      return masterDistributorsList;
    } else if (type === 'Distributors') {
      return distributorsList;
    } else if (type === 'Sub Distributors') {
      return subDistributorsList;
    } else if (type === 'Agents') {
      return agentsList;
    } else if (type === 'Retailers') {
      return retailerList;
    } else if (type === 'Customers') {
      return customerList;
    }
    return '';
  };

  const SEARCH_PLACEHOLDERS = [
    'user name',
    'phone number',
    'user code'
  ] as const;

  const rotatePlaceholder = useCallback(() => {
    setIsTransitioning(true);

    setTimeout(() => {
      setSearchPlaceholder(prev => {
        const currentIndex = SEARCH_PLACEHOLDERS.indexOf(prev as typeof SEARCH_PLACEHOLDERS[number]);
        const nextIndex = (currentIndex + 1) % SEARCH_PLACEHOLDERS.length; // Loop cyclically
        return SEARCH_PLACEHOLDERS[nextIndex];
      });
      setIsTransitioning(false);
    }, 500);
  }, []);

  useEffect(() => {
    const interval = setInterval(rotatePlaceholder, 2500);
    return () => clearInterval(interval);
  }, [rotatePlaceholder]);

  const TableDetails: Record<string, TableDetailsItem> = {
    "User Code": {
      id: "user_code",
      className: "min-w-[120px]"
    },
    "User Name": {
      sort: true,
      id: "name",
      custom: ["first_name", "last_name"],
      className: "min-w-[170px]"
    },
    "User Location": {
      id: "user_location",
      custom: ["country", "province", "district"],
    },
    "Created Date": {
      sort: true,
      id: "created_date",
      custom: ["created_at"],
    },
    "Phone Number": {
      id: "phone_number",
      custom: ["country_code", "phone_number"],
    },
    "Email Address": {
      id: "email_address",
    },
    ...(type !== 'Customers' ? {
      "Service Fee Type": {
        id: "service_fee_type",
        className: "min-w-[100px]",
      },
    } : {
      "Customer Type": {
        id: "customer_type",
        className: "min-w-[100px]",
      }
    }),
    "Last Login Date": {
      id: "last_login_in",
    },
    "Profile Status": {
      id: "profile",
      className: "min-w-[80px]",
    },
    "User Activity": {
      id: "user_activity",
      className: "min-w-[80px]",
    },
    "Status": {
      id: "status",
      toggle: true,
      className: "min-w-[100px]",
    },
    Actions: {
      id: "actions",
      className: "min-w-[200px]",
      actionTypes: ["edit", "view"],
    },
  };

  useEffect(() => {
    dispatch(setUserUrl(null));
    dispatch(setPreviousUrl(currentUrl));
    dispatch(resetPhoneNumbers());
  }, [currentUrl]);

  const handleActions = async (id: string, key: string, text?: string) => {
    const actions = {
      edit: () => navigation(`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/edit-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}`),
      view: () => navigation(`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}`),
      status: async () => {
        try {
          const payload = {
            status: !text,
            ...(text && { deactivate_reason: text })
          };

          const res = await PatchAPI(`${type.toLowerCase().replace(/ /g, '-')}/${id}`, payload);
          dispatch(showToast({
            type: res.error ? "error" : "success",
            message: res.error
              ? res.data.response.data.message
              : `${type.slice(0, -1)} ${text ? "deactivated" : "activated"} successfully`
          }));
          setFetchListTrigger(!fetchListTrigger)
        } catch (error) {
          dispatch(showToast({
            type: "error",
            message: "An unexpected error occurred. Please try again later.",
          }));
          console.error(`Error updating ${type.toLowerCase()} status:`, error);
        }
      }
    };

    await (actions[key as keyof typeof actions]?.() ?? Promise.resolve());
  };

  const navigateToAdd = () => {
    navigation(`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/add-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}`);
  };

  const staticText = {
    emptyScreenText: {
      heading1: `There are no ${type.toLowerCase()} added yet`,
      heading2: `Click “Add ${type.slice(0, -1)}” to create one`,
    },
  };

  return (
    <div>
      <Breadcrumbs
        navPath={["User Management", `${type.slice(0, -1)} Management`, `List of ${type}`]}
        heading={`List of ${type}`}
        noNavigation
        links={[`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`, `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`]}
        button={{
          buttonName: `Add ${type.slice(0, -1)}`,
          className: "px-4 whitespace-nowrap",
          buttonOnclick: navigateToAdd,
        }}
        hasAddPrivilege={privilegeObject.add}
        list={true}
      />
      <MainTable
        staticText={staticText}
        mainEndPoint={handleCurrentTab()}
        TableDetails={TableDetails}
        handleActions={handleActions}
        params={{ 'page_number': '1' }}
        searchPlaceholder={`Search by ${searchPlaceholder}`}
        placeholderClassName={`search-placeholder ${isTransitioning ? 'transitioning' : ''}`}
        filters={true}
        serviceNow={type === 'Customers' ? false : true}
        exporting={true}
        notfoundRoute={{ state: { route: `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management` } }}
        title={`${type} Management`}
        hasListPrivilege={testingProp || privilegeObject.list}
        hasActivateDeactivateMDPrivilege={privilegeObject.activateDeactivate}
        hasViewMDPrivilege={privilegeObject.view}
        hasEditMDPrivilege={privilegeObject.edit}
        fetchListTrigger={fetchListTrigger}
      />
    </div>
  );
}